import { formatDistanceToNowStrict } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Trophy, XCircle } from "lucide-react";
import type { Opportunity } from "@/hooks/useOpportunities";

export const formatBRL = (value: number) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

interface MobileOpportunityCardProps {
  opportunity: Opportunity;
  onClick: (opportunity: Opportunity) => void;
}

export const MobileOpportunityCard = ({ opportunity, onClick }: MobileOpportunityCardProps) => {
  const isWon = opportunity.status === "won";
  const isLost = opportunity.status === "lost";

  const updatedAgo = opportunity.updated_at
    ? formatDistanceToNowStrict(new Date(opportunity.updated_at), {
        addSuffix: true,
        locale: ptBR,
      })
    : null;

  const accentBorder = isWon
    ? "border-l-success"
    : isLost
      ? "border-l-destructive"
      : "border-l-accent-blue";

  return (
    <button
      type="button"
      onClick={() => onClick(opportunity)}
      className={`w-full rounded-xl border border-border border-l-4 ${accentBorder} bg-card p-3.5 text-left shadow-sm transition-transform active:scale-[0.99]`}
    >
      <div className="flex items-start justify-between gap-2">
        <p className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground">
          {opportunity.contact_name || opportunity.title || "Contato sem nome"}
        </p>
        {isWon && <Trophy className="h-4 w-4 shrink-0 text-success" />}
        {isLost && <XCircle className="h-4 w-4 shrink-0 text-destructive" />}
      </div>

      {(opportunity.product || opportunity.title) && (
        <p className="mt-0.5 truncate text-[13px] text-muted-foreground">
          {opportunity.product || opportunity.title}
        </p>
      )}

      <div className="mt-2.5 flex items-center justify-between gap-2">
        <span className="text-[14px] font-semibold text-foreground">
          {formatBRL(opportunity.price)}
        </span>
        {updatedAgo && (
          <span className="text-[11px] text-muted-foreground">{updatedAgo}</span>
        )}
      </div>

      {isLost && opportunity.loss_reason_name && (
        <p className="mt-1.5 truncate text-[12px] text-destructive/90">
          Motivo: {opportunity.loss_reason_name}
        </p>
      )}

      {opportunity.tags && opportunity.tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {opportunity.tags.slice(0, 3).map((tag) => (
            <span
              key={tag}
              className="rounded-full bg-card-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground"
            >
              {tag}
            </span>
          ))}
          {opportunity.tags.length > 3 && (
            <span className="rounded-full bg-card-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              +{opportunity.tags.length - 3}
            </span>
          )}
        </div>
      )}
    </button>
  );
};
